import React, { useRef, useState, useCallback } from "react";
import { Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Table, { ASCEND } from "@components/Table";
import { hasAccess } from "@components/Authorization";
import { indexOrganizationPublicKeysOperation as indexOperation } from "@api/services/identity";
import { type OrganizationPublicKey } from "@api/types";

import useColumnsConfig from "./helpers/useColumnsConfig";
import OrganizationPublicKeyGuideModal from "./OrganizationPublicKeyGuideModal";
import OrganizationPublicKeyCreatedModal from "./OrganizationPublicKeyCreatedModal";
import OrganizationPublicKeyDetailsModal from "./OrganizationPublicKeyDetailsModal";
import OrganizationPublicKeyCreateModal, {
  type OrganizationPublicKeyCreateModalHandle,
} from "./OrganizationPublicKeyCreateModal";

const LABEL_TITLE = "Public Keys";
const LABEL_CREATE = "Add Public Key";
const LABEL_GUIDE = "See how to create a key pair";
const DESCRIPTION = "Public keys let your client call the API without a browser. Your client signs an ID token with its private key, and the platform verifies that token with the public key you add here.";

const PERMISSION_WRITE = "organization-public-keys-write";

type TableHandle = {
  addItem: (item: OrganizationPublicKey) => void;
  updateItem: (
    id: string,
    attributes: Partial<OrganizationPublicKey>,
  ) => void;
};

const OrganizationPublicKeysTable = (): JSX.Element => {
  const table = useRef<TableHandle>({} as TableHandle);
  const createModal = useRef<
    OrganizationPublicKeyCreateModalHandle | Record<string, never>
  >({});

  const [ createdPublicKey, setCreatedPublicKey ] =
    useState<OrganizationPublicKey>();

  const [ selectedPublicKey, setSelectedPublicKey ] =
    useState<OrganizationPublicKey>();

  const [ isGuideOpen, setIsGuideOpen ] = useState(false);

  const onCreated = (newPublicKey: OrganizationPublicKey) => {
    table.current.addItem(newPublicKey);
    setCreatedPublicKey(newPublicKey);
  };

  // NOTE: the columns config keeps its identity while these handlers keep
  //       theirs, and a new config makes the table load the items again.
  const onRevoked = useCallback((revokedPublicKey: OrganizationPublicKey) =>
    table.current.updateItem(revokedPublicKey.id, revokedPublicKey)
  , []);

  const onSelected = useCallback((newSelectedPublicKey: OrganizationPublicKey) =>
    setSelectedPublicKey(newSelectedPublicKey)
  , []);

  const closeCreatedModal = () => setCreatedPublicKey(undefined);

  const closeDetailsModal = () => setSelectedPublicKey(undefined);

  const openGuideModal = () => setIsGuideOpen(true);

  const closeGuideModal = () => setIsGuideOpen(false);

  const openCreateModal = () => createModal.current.open?.();

  const columnsConfig = useColumnsConfig({ onSelected, onRevoked });

  const canCreate = hasAccess([ PERMISSION_WRITE ]);

  const createAction = {
    icon: <PlusOutlined />,
    action: openCreateModal,
    title: LABEL_CREATE,
  };

  const headerActions = canCreate
    ? [ createAction ]
    : [];

  const tableProps = {
    size: "small" as const,
    table,
    className: "last-column-action",
    headerActions,
    columnsConfig,
    indexOperation,
    defaultSortColumn: "name",
    defaultSortColumnDirection: ASCEND,
  };

  return (
    <>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {LABEL_TITLE}
      </Typography.Title>

      <Typography.Paragraph type="secondary">
        {DESCRIPTION}

        {canCreate && (
          <>
            {" "}

            <Typography.Link onClick={openGuideModal}>
              {LABEL_GUIDE}
            </Typography.Link>
          </>
        )}
      </Typography.Paragraph>

      <Table {...tableProps} />

      {canCreate && (
        <OrganizationPublicKeyCreateModal
          modal={createModal}
          onSuccess={onCreated}
        />
      )}

      <OrganizationPublicKeyCreatedModal
        isOpen={!!createdPublicKey}
        name={createdPublicKey?.name}
        issuer={createdPublicKey?.issuer}
        publicKeyId={createdPublicKey?.id}
        onClose={closeCreatedModal}
      />

      <OrganizationPublicKeyDetailsModal
        isOpen={!!selectedPublicKey}
        publicKey={selectedPublicKey}
        onClose={closeDetailsModal}
      />

      <OrganizationPublicKeyGuideModal
        isOpen={isGuideOpen}
        onClose={closeGuideModal}
      />
    </>
  );
};

export default OrganizationPublicKeysTable;
